import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { LogOut, BookOpen, Mail, BarChart2, ChevronRight, File, XCircle, Clock } from 'lucide-react';
import { getCurrentUser, logoutUser as logout } from '../utils/profilesAPI';
import { getAllExams } from '../utils/examAPI';
import { getAllSubjects } from '../utils/subjectsAPI';
import { getLearningStats, getUserSubjectHistory } from '../utils/subjectHistorysAPI';

/**
 * 个人中心页面组件
 * 展示用户信息、学习积分、统计数据和考试记录
 * @param {Object} session - 用户会话信息
 * @returns {JSX.Element} 渲染个人中心页面
 */
function Profile({ session }) {
  const [userProfile, setUserProfile] = useState(null);
  const [subjectExamHistory, setSubjectExamHistory] = useState([]);
  const [exams, setExams] = useState({});
  const [subjects, setSubjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [learningStats, setLearningStats] = useState({
    learningDays: 0,
    totalQuestions: 0,
    averageScore: 0,
    overallAccuracy: 0,
    recordsCount: 0,
    lastStudyDate: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        console.log("开始获取用户数据");
        
        // 尝试多种方式获取用户ID
        let userId = null;
        
        // 1. 尝试获取用户资料 - 这是最可靠的来源
        const profile = await getCurrentUser(session);
        if (profile) {
          setUserProfile(profile);
          // 使用_id作为唯一用户主键
          if (profile._id) {
            userId = profile._id;
            console.log("从用户资料获取主键ID:", userId);
          }
        } else {
          console.log("没有找到用户资料");
        }
        
        // 2. 尝试从session获取
        if (!userId && session?.user?.id) {
          userId = session.user.id;
          console.log("从会话获取用户ID:", userId);
        }
        
        // 3. 尝试从localStorage获取
        if (!userId) {
          const localStorageId = localStorage.getItem('user_id');
          if (localStorageId) {
            userId = localStorageId;
            console.log("从本地存储获取用户ID:", userId);
          }
        }
        
        // 如果仍然没有找到有效的用户ID，使用演示模式
        if (!userId) {
          console.log("没有找到有效的用户ID，使用演示模式");
          userId = 'demo-user';
          const demoUser = {
            _id: userId,
            nickname: '演示用户'
          };
          setUserProfile(demoUser);
        }
        
        console.log("最终使用的用户ID:", userId);
        
        // 保存到localStorage
        if (userId && userId !== 'demo-user') {
          localStorage.setItem('user_id', userId);
        }
        
        // 1. 获取所有考试信息，建立ID到名称的映射
        try {
          console.log("获取所有考试信息");
          const examsData = await getAllExams();
          
          if (examsData) {
            console.log(`获取到${examsData.length}个考试信息`);
            const examsMap = {};
            examsData.forEach(exam => {
              examsMap[exam._id] = exam.name;
            });
            setExams(examsMap);
          }
        } catch (err) {
          console.error("获取考试信息异常:", err);
        }
        
        // 2. 获取所有科目信息，建立ID到名称的映射
        try {
          console.log("获取所有科目信息");
          const subjectsData = await getAllSubjects();
          
          if (subjectsData) {
            console.log(`获取到${subjectsData.length}个科目信息`);
            const subjectsMap = {};
            subjectsData.forEach(subject => {
              subjectsMap[subject._id] = subject.name;
            });
            setSubjects(subjectsMap);
          }
        } catch (err) {
          console.error("获取科目信息异常:", err);
        }
        
        // 3. 获取科目考试历史记录
        try {
          console.log("获取科目考试历史, 用户ID:", userId);
          
          if (userId) {
            // 使用 getUserSubjectHistory 获取用户的科目考试历史记录
            // 在subject_historys表中，用户ID字段是user_id，应该与profiles的_id对应
            const allSubjectHistoryData = await getUserSubjectHistory(userId);
            
            console.log("获取到用户考试历史:", allSubjectHistoryData?.length || 0, "条记录");
            if (allSubjectHistoryData && allSubjectHistoryData.length > 0) {
              console.log("科目历史记录样本数据结构:", Object.keys(allSubjectHistoryData[0]));
              console.log("科目历史记录样本:", allSubjectHistoryData[0]);
              
              // 设置科目考试历史数据
              setSubjectExamHistory(allSubjectHistoryData || []);
            } else {
              console.log("没有科目考试历史记录");
              setSubjectExamHistory([]);
              
              // 如果第一次查询没有结果，尝试使用不同的ID格式 (增加容错性)
              try {
                console.log("尝试使用备用方式查询历史记录");
                // 针对数字ID和字符串ID可能的差异
                const idToTry = isNaN(userId) ? userId : Number(userId);
                const altHistoryData = await getUserSubjectHistory(idToTry);
                
                if (altHistoryData && altHistoryData.length > 0) {
                  console.log(`备用查询获取到${altHistoryData.length}条历史记录`);
                  setSubjectExamHistory(altHistoryData);
                }
              } catch (backupErr) {
                console.error("备用查询出错:", backupErr);
              }
            }
            
            // 获取学习统计数据 (getLearningStats 返回的是统计对象，不是数组)
            try {
              const statsData = await getLearningStats(userId);
              console.log("获取学习统计数据:", statsData);
              // 将学习统计数据存储到状态中
              if (statsData) {
                setLearningStats(statsData);
              }
              
              // 如果没有获取到考试历史但有统计数据，尝试再次使用备用方式获取历史
              if ((!allSubjectHistoryData || allSubjectHistoryData.length === 0) && 
                  statsData && statsData.recordsCount > 0) {
                try {
                  console.log("从统计数据发现有记录但未获取到历史，尝试额外的备用查询");
                  // 尝试使用不同形式的userId (字符串/数字/去除引号等)
                  const cleanId = userId.toString().replace(/['"]/g, '');
                  const extraQuery = await getUserSubjectHistory(cleanId);
                  
                  if (extraQuery && extraQuery.length > 0) {
                    console.log(`额外备用查询获取到${extraQuery.length}条历史记录`);
                    setSubjectExamHistory(extraQuery);
                  }
                } catch (extraErr) {
                  console.error("额外备用查询出错:", extraErr);
                }
              }
            } catch (statsErr) {
              console.error("获取学习统计数据出错:", statsErr);
            }
          } else {
            console.log("没有用户ID，无法获取科目考试历史");
            setSubjectExamHistory([]);
          }
        } catch (err) {
          console.error("获取科目考试历史异常:", err);
          setSubjectExamHistory([]);
        }
        
      } catch (error) {
        console.error("数据加载过程中出现错误:", error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [session, navigate]);

  // 计算统计数据 - 添加容错处理
  const getTotalExamCount = () => {
    try {
      // 优先使用学习统计数据中的记录数
      if (learningStats && learningStats.recordsCount > 0) {
        return learningStats.recordsCount;
      }
      // 备用方案：使用直接获取的历史记录长度
      return subjectExamHistory?.length || 0;
    } catch (e) {
      console.error("计算考试次数错误:", e);
      return 0;
    }
  };
  
  const getPassCount = () => {
    try {
      return subjectExamHistory.filter(h => (parseInt(h.score) || 0) >= 60).length;
    } catch (e) {
      console.error("计算及格次数错误:", e);
      return 0;
    }
  };

  const handleLogout = async () => {
    try {
      // 使用profilesAPI的logoutUser函数
      await logout(session);
      
      // 清除本地存储的用户ID
      localStorage.removeItem('user_id');
      
      // 使用window.location直接跳转到登录页，完全重新加载应用
      // 这样可以避免React路由中的状态问题
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error.message);
      // 发生错误时也尝试重定向
      window.location.href = '/login';
    }
  };

  // 确保有数据可显示
  const ensureArray = (arr) => Array.isArray(arr) ? arr : [];
  const safeSubjectExamHistory = ensureArray(subjectExamHistory);

  // 获取考试名称
  const getExamName = (examId) => {
    return exams[examId] || '未知考试';
  };
  
  // 获取科目名称
  const getSubjectName = (subjectId) => {
    return subjects[subjectId] || '未知科目';
  };

  // 计算学习天数 - 基于学习统计或考试次数
  const getStudyDays = () => {
    if (learningStats && learningStats.learningDays > 0) {
      return learningStats.learningDays;
    }
    if (getTotalExamCount() === 0) {
      return 0; // 没有考试记录时返回0
    }
    // 有考试记录时，计算学习天数
    return Math.min(365, Math.max(1, getTotalExamCount() * 3));
  };

  // 获取连续答题天数
  const getConsecutiveDays = () => {
    if (getTotalExamCount() === 0) {
      return 0; // 没有考试记录时返回0
    }
    
    // 有考试记录时，返回连续答题天数（上限为7天）
    return Math.min(7, getStudyDays());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-bg-color pb-16">
      <Header title="个人中心" showBack={false} />

      {/* 主要功能区 */}
      <div className="px-4 pt-5 pb-10">
        {/* 用户欢迎消息 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-5">
          <h2 className="font-medium text-lg text-gray-800">
            欢迎, {userProfile?.nickname || userProfile?.username || '同学'}
          </h2>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-5">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <BookOpen className="w-6 h-6 text-blue-500 mr-3" />
              <h3 className="font-medium text-lg text-gray-800">学习成就</h3>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-4 gap-4">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                  <span className="text-amber-500 text-xl font-bold">{getTotalExamCount()}</span>
                </div>
                <span className="text-sm text-gray-600">考试数</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-2">
                  <span className="text-green-500 text-xl font-bold">{getPassCount()}</span>
                </div>
                <span className="text-sm text-gray-600">及格数</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                  <span className="text-blue-500 text-xl font-bold">{getConsecutiveDays()}</span>
                </div>
                <span className="text-sm text-gray-600">连续答题</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                  <span className="text-purple-500 text-xl font-bold">
                    {safeSubjectExamHistory.filter(h => (parseInt(h.score) || 0) === 100).length}
                  </span>
                </div>
                <span className="text-sm text-gray-600">满分数</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-5">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-secondary mr-3" />
                <h3 className="font-medium text-gray-800">近期考试记录</h3>
              </div>
              <button 
                onClick={() => navigate('/study-report')}
                className="text-gray-500 flex items-center text-xs"
              >
                查看全部 <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {getTotalExamCount() > 0 ? (
            <div className="divide-y divide-gray-100">
              {safeSubjectExamHistory
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .slice(0, 3)
                .map((history, index) => (
                <div 
                  key={history._id || history.id || `history-${index}`} 
                  className="p-4 active:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/exam-answer-detail/${history.exam_id || '0'}/${history.subject_id || '0'}/${history._id || history.id}`)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium text-gray-800">
                        {history.subject_id 
                          ? getSubjectName(history.subject_id)
                          : history.exam_id 
                            ? getExamName(history.exam_id)
                            : '未知考试'}
                      </h4>
                      <div className="flex text-xs text-gray-500 mt-1">
                        <span className="mr-3">答对: {history.correct_count || 0}/{history.total_questions || history.question_count || 0}</span>
                        <span>{history.created_at ? new Date(history.created_at).toLocaleDateString() : '未知日期'}</span>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      (parseInt(history.score) || 0) >= 60 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {history.score || 0}分
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                <File className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">暂无考试记录</p>
            </div>
          )}
        </div>

        {/* 功能菜单 */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-5">
          <div className="divide-y divide-gray-100">
            <button 
              onClick={() => navigate('/mistakes')}
              className="w-full p-4 flex items-center justify-between"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <span className="text-gray-800">我的错题集</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
            
            <button 
              onClick={() => navigate('/study-report')}
              className="w-full p-4 flex items-center justify-between"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <BarChart2 className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-gray-800">学习报告</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
            
            <button 
              onClick={() => navigate('/messages')}
              className="w-full p-4 flex items-center justify-between"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                  <Mail className="w-5 h-5 text-yellow-500" />
                </div>
                <span className="text-gray-800">消息通知</span>
              </div>
              <div className="flex items-center">
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full mr-2">3</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>
          </div>
        </div>

        {/* 退出登录按钮 */}
        <button 
          onClick={handleLogout}
          className="w-full bg-white p-4 rounded-xl text-red-500 font-medium shadow-sm flex items-center justify-center"
        >
          <LogOut className="w-5 h-5 mr-2" />
          退出登录
        </button>
      </div>
      
      <TabBar />
    </div>
  );
}

export default Profile; 